import React, { useEffect, useState, useContext } from "react";
import { BarChart } from '@mui/x-charts/BarChart';
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import { applySalesMultiplier } from '../utils/chartUtils';
import { 
  StandardChartCard,
  useResponsiveChartProps,
  getXAxisConfig,
  getYAxisConfig,
  getImpactSeriesConfig
} from './common/ChartDefaults';
import { useMediaQuery, useTheme, Autocomplete, TextField } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';

const capitalizeSubject = (subject_group) => {
  return subject_group.charAt(0).toUpperCase() + subject_group.slice(1);
};

const SubjectsByImpactChart = ({
  showTitle = true,
  showSearch = true,
  // Optional props for dummy data
  businessData = null,
  reviewsDataOverride = null,
  viewOverride = null,
  title = "Impact by Subject"
}) => {
  // If props aren't provided, use real context:
  const businessContext = useContext(BusinessContext);
  const reviewsContext = useContext(ReviewsContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { chartSx, slotProps, margin } = useResponsiveChartProps();

  // Decide which data to use
  // Prioritize props if they exist
  const finalSelectedBusiness = businessData?.selectedBusiness !== undefined
    ? businessData.selectedBusiness
    : businessContext?.selectedBusiness;
  const finalCurrencySymbol = businessData?.currencySymbol !== undefined
    ? businessData.currencySymbol
    : businessContext?.currencySymbol || "$";
  const finalReviewsData = reviewsDataOverride !== null
    ? reviewsDataOverride
    : reviewsContext?.reviewsData || [];
  const finalView = viewOverride !== null
    ? viewOverride
    : reviewsContext?.view || "Rating";

  // For context values that are *not* overridden by props, we still need to get them from context:
  const {
    selectedCategory = reviewsContext?.selectedCategory || null,
    setSelectedSubject = reviewsContext?.setSelectedSubject || (() => {}),
    setSelectedAttribute = reviewsContext?.setSelectedAttribute || (() => {}),
    selectedSubject = reviewsContext?.selectedSubject || null,
    setSelectedDate = reviewsContext?.setSelectedDate || (() => {}),
    sortOption = reviewsContext?.sortOption || "total"
  } = reviewsContext || {}; // Keep this for non-overridden context values

  // ... rest of existing component state
  const [topSubjectsRaw, setTopSubjectsRaw] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [chartSubjects, setChartSubjects] = useState([]);
  const [positiveData, setPositiveData] = useState([]);
  const [negativeData, setNegativeData] = useState([]);
  const [subjectImpact, setSubjectImpact] = useState({});

  // const { currencySymbol } = useContext(BusinessContext);
  const MAX_SUBJECTS = 8;

  useEffect(() => {
    try {
      const impactData = {};

      (finalReviewsData || []).forEach(review => { // Use finalReviewsData
        if (!review.analysis || review.analysis.length === 0) return;
        review.analysis.forEach(item => {
          // Only process analysis items that match the selected category (if one is selected)
          if (selectedCategory && (!item.category || item.category.toLowerCase() !== selectedCategory.toLowerCase())) {
            return;
          }
          const { subject_group, attribute_impact } = item; // Change from subject to subject_group
          if (!subject_group) return; // Change from subject to subject_group
          if (!impactData[subject_group]) { // Change from subject to subject_group
            impactData[subject_group] = { positive: 0, negative: 0, totalImpact: 0 }; // Change from subject to subject_group
          }
          if (attribute_impact > 0) {
            impactData[subject_group].positive += attribute_impact; // Change from subject to subject_group
          } else if (attribute_impact < 0) {
            impactData[subject_group].negative += attribute_impact; // Change from subject to subject_group
          }
          impactData[subject_group].totalImpact += Math.abs(attribute_impact); // Change from subject to subject_group
        });
      });

      setSubjectImpact(impactData);

      // Sort based on the selected sort option
      const sortedByImpact = Object.keys(impactData).sort((a, b) => {
        switch (sortOption) {
          case 'positive':
            return impactData[b].positive - impactData[a].positive;
          case 'negative':
            // Sort by negative impact (most negative first, so we use a - b, not b - a)
            return impactData[a].negative - impactData[b].negative;
          case 'total':
          default:
            return impactData[b].totalImpact - impactData[a].totalImpact;
        }
      });

      // Save the top 10 in their original (un-capitalized) form
      const topTenRaw = sortedByImpact.slice(0, MAX_SUBJECTS);
      setTopSubjectsRaw(topTenRaw);

      // Create a capitalized list of all subjects
      let allSubs = sortedByImpact.map(s => capitalizeSubject(s));
      // Sort alphabetically
      allSubs.sort((a, b) => a.localeCompare(b));
      // Save all subjects for Autocomplete
      setAllSubjects(allSubs);
    } catch (error) {
      console.error(error);
    }
  }, [finalSelectedBusiness, finalReviewsData, selectedCategory, sortOption]); // Update dependencies

  // This effect decides which subjects end up on the bar chart,
  // plus sets the data arrays for positive/negative bars.
  useEffect(() => {
    // We'll begin with top 10
    let finalSubjectsRaw = [...topSubjectsRaw]; // in original form

    // If a subject is selected and it's not already in the top 10, add it
    if (selectedSubject) {
      // Find the original form: we have capitalized selectedSubject from Autocomplete, so let's find the match
      const selectedOriginal = Object.keys(subjectImpact).find(
        s => capitalizeSubject(s) === selectedSubject
      );
      if (
        selectedOriginal &&
        !finalSubjectsRaw.includes(selectedOriginal)
      ) {
        finalSubjectsRaw.push(selectedOriginal);
      }
    }

    // Now build the data arrays in the same order
    const posData = [];
    const negData = [];

    finalSubjectsRaw.forEach(s => {
      posData.push(subjectImpact[s]?.positive || 0);
      negData.push(subjectImpact[s]?.negative || 0);
    });

    // Convert these to display form
    const adjustedPositiveData = applySalesMultiplier(posData, finalView, finalSelectedBusiness?.turnover); // Use finalView, finalSelectedBusiness
    const adjustedNegativeData = applySalesMultiplier(negData, finalView, finalSelectedBusiness?.turnover); // Use finalView, finalSelectedBusiness

    setPositiveData(adjustedPositiveData);
    setNegativeData(adjustedNegativeData);

    // Finally, capitalize the subjects for the chart labels
    setChartSubjects(finalSubjectsRaw.map(s => capitalizeSubject(s)));
  }, [topSubjectsRaw, subjectImpact, selectedSubject, finalView, finalSelectedBusiness?.turnover]); // Update dependencies

  const onAxisClick = (event, params) => {
    // Stop event propagation to prevent document click handler from clearing selections
    event.stopPropagation();
    
    setSelectedAttribute(null);
    setSelectedDate(null);
    const clickedIndex = params?.dataIndex;
    if (clickedIndex !== undefined) {
      const clickedSubject = chartSubjects[clickedIndex];
      if (clickedSubject) {
        console.log(`[SubjectsChart] Clicked on chart item: ${clickedSubject}`);
        
        // Check if the subject is already selected
        if (selectedSubject === clickedSubject) {
          // If already selected, unselect it
          setSelectedSubject(null);
          return;
        }
        
        // Otherwise, select this subject
        setSelectedSubject(clickedSubject);
      }
    }
  };

  return (
    <StandardChartCard 
      data-chart-type="subject-chart"
      data-filter-exempt="true"
      title={showTitle ? (selectedCategory ? `${selectedCategory} Subjects` : title) : null}
      titleIcon={<CategoryIcon />}
      action={showSearch && (
        <Autocomplete
          options={allSubjects}
          sx={{ 
            width: 120,
            ml: '10px',
            mr: '10px',
            mt: '10px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            }
          }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Search" 
              variant="outlined" 
              size="small"
              InputProps={{
                ...params.InputProps,
                style: { fontSize: '0.9rem' }
              }}
              InputLabelProps={{
                style: { fontSize: '0.9rem' }
              }}
            />
          )}
          ListboxProps={{
            style: { fontSize: '0.9rem' }
          }}
          PaperProps={{
            style: { fontSize: '0.9rem' }
          }}
          onChange={(e, newValue) => {
            // Stop event propagation to prevent document click handler from clearing selections
            if (e && e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation();
            }
            
            // Check if this subject is already selected
            if (newValue === selectedSubject) {
              // If already selected, unselect it
              setSelectedSubject(null);
            } else {
              // Otherwise set the new value
              setSelectedSubject(newValue || null);
            }
          }}
          value={selectedSubject || null}
          clearOnEscape
        />
      )}
    >
      <BarChart
        sx={chartSx}
        onAxisClick={onAxisClick}
        xAxis={[getXAxisConfig(chartSubjects, isSmallScreen)]}
        yAxis={[getYAxisConfig(finalCurrencySymbol, finalView, isSmallScreen)]} // Use finalCurrencySymbol, finalView
        series={getImpactSeriesConfig(positiveData, negativeData, finalCurrencySymbol, finalView)} // Use finalCurrencySymbol, finalView
        slotProps={{
          ...slotProps,
          root: {
            'data-chart-type': 'subject-chart',
            'data-filter-exempt': 'true'
          }
        }}
        margin={margin}
      />
    </StandardChartCard>
  );
};

export default SubjectsByImpactChart;
